import { configureStore } from '@reduxjs/toolkit';

import throttle from 'lodash/throttle';
import { APP_DEFAULT_LANGUAGE, APP_LANGUAGES_ENUM } from 'config/constants';
import { getFromLocalStorage, setToLocalStorage, removeFromLocalStorage } from 'core/services/localStorage.service';

// slices
import generalSlice from './slices/generalSlice';
import bibleTranslationsSlice from './slices/bibleTranslationsSlice';
import biblesSlice from './slices/biblesSlice';
import memosSlice from './slices/memos';
import bookmarksSlice from './slices/bookmarks';
import notesSlice from './slices/notes';
// selectors
import { authApi } from './slices/auth/service';
import authReducer, { initialState as initialAuthState } from './slices/auth';
import selectLanguages from './selectors/selectLanguages';
import { getBibleTrState } from './selectors/bibleTranslations';
import { getAuthUserInfo } from './selectors/auth';
import { getBiblesState } from './selectors/bibles';
// types
import { IBibleSliceState } from './slices/bibleTranslationsSlice/types';
import { IUserInfo } from './slices/auth/types';
import { IBibleState } from './slices/biblesSlice/types';

const BIBLE_TRANSLATIONS_KEY = 'bibleTranslations';
const APP_LANGUAGE_KEY = 'language';
const USER_INFO_KEY = 'user-info';
const BIBLES_KEY = 'bibles';

const persistedBibles = getFromLocalStorage<IBibleState>(BIBLES_KEY);
const persistedBibleTranslations = getFromLocalStorage<IBibleSliceState>(BIBLE_TRANSLATIONS_KEY);
const persistedAppLanguage = getFromLocalStorage<APP_LANGUAGES_ENUM>(APP_LANGUAGE_KEY);
const persistedUserInfo = getFromLocalStorage<IUserInfo>(USER_INFO_KEY);

const store = configureStore({
  reducer: {
    general: generalSlice.reducer,
    bibleTranslations: bibleTranslationsSlice.reducer,
    bibles: biblesSlice.reducer,
    memos: memosSlice.reducer,
    bookmarks: bookmarksSlice.reducer,
    notes: notesSlice.reducer,
    auth: authReducer,
    [authApi.reducerPath]: authApi.reducer,
  },
  middleware: getDefaultMiddleware => getDefaultMiddleware().concat(authApi.middleware),
  preloadedState: {
    general: { language: persistedAppLanguage || APP_DEFAULT_LANGUAGE },
    bibleTranslations: persistedBibleTranslations,
    bibles: persistedBibles,
    auth: {
      ...initialAuthState,
      ...(persistedUserInfo && { userInfo: persistedUserInfo }),
    },
  },
});

// listen for store changes and use setToLocalStorage to save them to localStorage
store.subscribe(throttle(() => {
  setToLocalStorage(BIBLE_TRANSLATIONS_KEY, getBibleTrState(store.getState()));
  setToLocalStorage(BIBLES_KEY, getBiblesState(store.getState()));
  setToLocalStorage(APP_LANGUAGE_KEY, selectLanguages(store.getState()));
  const userInfo = getAuthUserInfo(store.getState());
  userInfo ? setToLocalStorage(USER_INFO_KEY, userInfo) : removeFromLocalStorage(USER_INFO_KEY);
}, 1000));

export default store;
